// Story 6.1 (ADO-877) — typed, validated runtime Auth0 config from env (AC1, AC4).
// Story 6.14 (ADO-1186) — D2/D9 course-correction: Auth0 Organizations are not
// used (no `organization` key) and staff sign in through the shared custom-DB
// connection (no `staffConnection` key / enterprise-connection initiator).
//
// AC1: `domain`, `clientId`, and `audience` (dev: `https://shoplink2-api-dev.bwg.ie`,
// Story 6.32 / ADO-1315) are sourced from environment/runtime config — never
// hard-coded, no client secret. Vite exposes only `VITE_`-prefixed vars to the
// client bundle.
//
// AC4: when config is absent/incomplete (local), this resolver NEVER throws and
// NEVER falls back to a hard-coded value — it returns `{ configured: false }`
// so the shell can surface an explicit "authentication not configured" state and
// never establish a silent session (mirrors apps/api's local posture in
// AUTH_FLOW_SPEC §3, where JwtService.verify always throws when Auth0 is unset).

/** Validated, non-empty Auth0 SPA configuration. */
export interface Auth0RuntimeConfig {
  readonly domain: string;
  readonly clientId: string;
  /** The per-env API identifier — dev: `https://shoplink2-api-dev.bwg.ie` (BWG runbook §3b). */
  readonly audience: string;
  /**
   * Story 6.4 (ADO-880) — the Auth0 **database connection** name used by the
   * onboarding re-request (`POST /dbconnections/change_password`). Deloitte-owned
   * and OPTIONAL: present → the expired-link recovery surface can re-send a
   * set-password email; absent (`undefined`) → the re-request degrades to a
   * no-op send (the enumeration-safe confirmation is still shown). Deliberately
   * NOT in {@link REQUIRED_AUTH_ENV_KEYS}.
   */
  readonly dbConnection?: string;
}

/**
 * Discriminated result of {@link resolveAuthConfig}. `configured: false` carries
 * the list of missing env keys so the "not configured" UI and logs can be
 * precise without ever exposing a value.
 */
export type AuthConfigResult =
  | { readonly configured: true; readonly config: Auth0RuntimeConfig }
  | { readonly configured: false; readonly missing: readonly string[] };

/** The env keys that must all be present and non-empty for auth to be configured. */
export const REQUIRED_AUTH_ENV_KEYS = [
  'VITE_AUTH0_DOMAIN',
  'VITE_AUTH0_CLIENT_ID',
  'VITE_AUTH0_AUDIENCE',
] as const;

type AuthEnv = Record<string, string | boolean | undefined>;

function readTrimmed(env: AuthEnv, key: string): string {
  const raw = env[key];
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Resolve the Auth0 SPA config from Vite env. Pure and total: never throws,
 * never invents a default. Inject `env` in tests; defaults to `import.meta.env`.
 */
export function resolveAuthConfig(env: AuthEnv = import.meta.env): AuthConfigResult {
  const domain = readTrimmed(env, 'VITE_AUTH0_DOMAIN');
  const clientId = readTrimmed(env, 'VITE_AUTH0_CLIENT_ID');
  const audience = readTrimmed(env, 'VITE_AUTH0_AUDIENCE');

  const missing = REQUIRED_AUTH_ENV_KEYS.filter((key) => readTrimmed(env, key) === '');
  if (missing.length > 0) {
    return { configured: false, missing };
  }

  // Story 6.4 (ADO-880) — optional DB connection for the onboarding re-request.
  // Empty/absent → the key is OMITTED (not set to `undefined`) to satisfy
  // `exactOptionalPropertyTypes`; it never affects `configured` and adds no
  // throw path.
  const dbConnection = readTrimmed(env, 'VITE_AUTH0_DB_CONNECTION');

  return {
    configured: true,
    config: {
      domain,
      clientId,
      audience,
      ...(dbConnection !== '' ? { dbConnection } : {}),
    },
  };
}
